/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowRightOutlined, WarningOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';

import { AlertConfig } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { destinationDisplayNames, monoTagStyle } from './constants';

dayjs.extend(utc);

interface AlertConfigDetailsOverviewProps {
  alert: AlertConfig;
}

export function AlertConfigDetailsOverview({ alert }: AlertConfigDetailsOverviewProps) {
  const { t } = useMimicTranslation('notificationManagement');

  const nodeMap = useMemo(() => {
    if (!alert?.nodeDetails) return new Map<string, string>();
    return new Map(alert.nodeDetails.map((n: { id: string; name: string }) => [n.id, n.name]));
  }, [alert?.nodeDetails]);

  const displayName = destinationDisplayNames[alert.type] || alert.type;

  let webhookUrl: string | undefined;
  if (alert.config && 'slackWebhook' in alert.config) {
    webhookUrl = alert.config.slackWebhook;
  } else if (alert.config && 'exabeamWebhook' in alert.config) {
    webhookUrl = alert.config.exabeamWebhook;
  } else if (alert.config && 'splunkWebhook' in alert.config) {
    webhookUrl = alert.config.splunkWebhook;
  }

  const createdDate = `${dayjs(alert.dateCreated).utc().format('D MMM YYYY h:mmA')} (UTC)`;
  const isActive = alert.status === 'active';

  return (
    <Container gap="sm" full style={{ width: '100%' }}>
      <div className="alert-config-details-meta">
        <span className="alert-config-status-badge">
          <span
            className={`alert-config-status-dot ${isActive ? 'alert-config-status-dot--active' : 'alert-config-status-dot--suspended'}`}
          />
          {isActive ? t('details.active') : t('details.suspended')}
        </span>
        <span className="alert-config-details-meta-separator">|</span>
        <Text size="md">
          <ArrowRightOutlined style={{ marginRight: '6px' }} />
          {displayName}
        </Text>
        <span className="alert-config-details-meta-separator">|</span>
        <Text size="md">
          {t('details.created')} {createdDate}
        </Text>
      </div>

      {alert.description && (
        <div className="alert-config-details-description">
          <Text type="subtle" size="md">
            {alert.description}
          </Text>
        </div>
      )}

      <div className="alert-config-details-sections">
        {webhookUrl && (
          <div className="alert-config-details-section">
            <Title level={4} style={{ marginBottom: '8px' }}>
              {t('details.connection')}
            </Title>
            <Text size="md">{webhookUrl}</Text>
          </div>
        )}

        <div className="alert-config-details-section">
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('details.eventTypes')}
          </Title>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {alert.eventTypes.map((eventType) => (
              <div key={eventType} className="alert-config-event-type-row">
                <div className="alert-config-event-type-info">
                  <Text strong size="md">
                    {t(`parameters.events.${eventType}`)}
                  </Text>
                  <br />
                  <Text type="subtle" size="sm">
                    {t(`parameters.events.${eventType}Description`)}
                  </Text>
                  {eventType === 'signal' && (
                    <div className="alert-config-event-type-warning">
                      <WarningOutlined style={{ color: '#d4b25a', fontSize: '13px' }} />
                      <Text size="sm" style={{ color: '#d4b25a' }}>
                        {t('details.signalWarning')}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="alert-config-details-section">
          <Title level={4} style={{ marginBottom: '8px' }}>
            {t('details.nodes')}
          </Title>
          <div className="alert-config-chips">
            {alert.nodeIds.length > 0 ? (
              alert.nodeIds.map((nodeId) => (
                <Tag key={nodeId} style={monoTagStyle}>
                  {nodeMap.get(nodeId) || nodeId}
                </Tag>
              ))
            ) : (
              <Text type="subtle" size="sm">
                {t('details.allNodes')}
              </Text>
            )}
          </div>
        </div>

        {alert.tags && alert.tags.length > 0 && (
          <div className="alert-config-details-section">
            <Title level={4} style={{ marginBottom: '8px' }}>
              {t('details.tags')}
            </Title>
            <div className="alert-config-chips">
              {alert.tags.map((tag) => (
                <Tag key={tag} style={monoTagStyle}>
                  {tag}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {alert.keyValueFilters && Object.keys(alert.keyValueFilters.include || {}).length > 0 && (
          <div className="alert-config-details-section">
            <Title level={4} style={{ marginBottom: '8px' }}>
              {t('details.includedKeyValuePairs')}
            </Title>
            <div className="alert-config-chips">
              {Object.entries(alert.keyValueFilters.include || {}).map(([key, value]) => (
                <Tag key={key} style={monoTagStyle}>
                  {key}:{String(value)}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {alert.keyValueFilters && Object.keys(alert.keyValueFilters.exclude || {}).length > 0 && (
          <div className="alert-config-details-section">
            <Title level={4} style={{ marginBottom: '8px' }}>
              {t('details.excludedKeyValuePairs')}
            </Title>
            <div className="alert-config-chips">
              {Object.entries(alert.keyValueFilters.exclude || {}).map(([key, value]) => (
                <Tag key={key} style={monoTagStyle}>
                  {key}:{String(value)}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
